import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import "./Footer.scss";
import emil from "../../Assets/mail.png";
import call from "../../Assets/call.png";
import location from "../../Assets/location_on.png";
import { usePlatformSettingsPublic } from "../../hooks/usePlatformSettingsPublic";

function FooterLink({ to, children, className }) {
  const cls = [className || "healbid-foot__a"].filter(Boolean).join(" ");
  if (String(to).startsWith("mailto:") || String(to).startsWith("http")) {
    return (
      <a href={to} className={cls}>
        {children}
      </a>
    );
  }
  return (
    <Link to={to} className={cls}>
      {children}
    </Link>
  );
}

function SocialIcon({ label }) {
  const key = String(label || "").toLowerCase();
  if (key.includes("facebook")) {
    return (
      <svg viewBox="0 0 24 24" fill="none">
        <path
          d="M14 8.5V7.2c0-.7.5-1.2 1.2-1.2H17V3h-2.2C12.7 3 11 4.7 11 6.8v1.7H9v3h2V21h3v-9.5h2.6l.4-3H14z"
          fill="currentColor"
        />
      </svg>
    );
  }
  if (key.includes("instagram")) {
    return (
      <svg viewBox="0 0 24 24" fill="none">
        <path
          d="M7.5 3h9A4.5 4.5 0 0 1 21 7.5v9A4.5 4.5 0 0 1 16.5 21h-9A4.5 4.5 0 0 1 3 16.5v-9A4.5 4.5 0 0 1 7.5 3zm9 2h-9A2.5 2.5 0 0 0 5 7.5v9A2.5 2.5 0 0 0 7.5 19h9a2.5 2.5 0 0 0 2.5-2.5v-9A2.5 2.5 0 0 0 16.5 5z"
          fill="currentColor"
        />
        <path
          d="M12 8a4 4 0 1 1 0 8 4 4 0 0 1 0-8zm0 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4z"
          fill="currentColor"
        />
        <path d="M17.5 6.5a1 1 0 1 1 0 2 1 1 0 0 1 0-2z" fill="currentColor" />
      </svg>
    );
  }
  if (key === "x" || key.includes("twitter")) {
    return (
      <svg viewBox="0 0 24 24" fill="none">
        <path
          d="M18.8 3H21l-6.9 7.9L22 21h-6.2l-4.9-6.1L5.6 21H3.4l7.4-8.5L2 3h6.3l4.4 5.6L18.8 3zm-1.1 16h1.6L7.2 4.9H5.5L17.7 19z"
          fill="currentColor"
        />
      </svg>
    );
  }
  if (key.includes("linkedin")) {
    return (
      <svg viewBox="0 0 24 24" fill="none">
        <path
          d="M6.5 6.5A1.5 1.5 0 1 1 6.5 3a1.5 1.5 0 0 1 0 3.5zM5 8h3v13H5V8zm6 0h2.9v1.8h.1c.4-.8 1.5-2 3.4-2 3 0 3.6 2 3.6 4.7V21h-3v-6.2c0-1.5 0-3.3-2-3.3s-2.3 1.6-2.3 3.2V21h-3V8z"
          fill="currentColor"
        />
      </svg>
    );
  }
  if (key.includes("youtube")) {
    return (
      <svg viewBox="0 0 24 24" fill="none">
        <path
          d="M21.6 7.2s-.2-1.6-.8-2.3c-.8-.8-1.7-.8-2.1-.9C15.8 3.8 12 3.8 12 3.8h0s-3.8 0-6.7.2c-.4 0-1.3.1-2.1.9-.6.7-.8 2.3-.8 2.3S2 9 2 10.8v1.7c0 1.8.4 3.6.4 3.6s.2 1.6.8 2.3c.8.8 1.9.8 2.4.9 1.7.2 6.4.2 6.4.2s3.8 0 6.7-.2c.4 0 1.3-.1 2.1-.9.6-.7.8-2.3.8-2.3s.4-1.8.4-3.6v-1.7c0-1.8-.4-3.6-.4-3.6z"
          fill="currentColor"
        />
        <path d="M10 15.3V8.7L16 12l-6 3.3z" fill="#0b3a34" />
      </svg>
    );
  }
  return <span>{String(label || "?").slice(0, 1).toUpperCase()}</span>;
}

/** @param {{ supportPhoneTel?: string, supportPhoneDisplay?: string }} f */
function phoneHrefFromFooter(f) {
  const tel = String(f?.supportPhoneTel || "").trim();
  if (tel) {
    const norm = tel.replace(/[^\d+]/g, "");
    return norm.startsWith("+") ? `tel:${norm}` : `tel:${norm.replace(/^\+/, "")}`;
  }
  const d = String(f?.supportPhoneDisplay || "").trim();
  if (!d) return "";
  const digits = d.replace(/[^\d+]/g, "");
  const n = digits.startsWith("+") ? digits : digits ? `+${digits.replace(/^\+/, "")}` : "";
  return n ? `tel:${n.replace(/\s/g, "")}` : "";
}

const FALLBACK_SOCIAL = [
  { label: "Facebook", url: "https://www.facebook.com/" },
  { label: "Instagram", url: "https://www.instagram.com/" },
  { label: "X", url: "https://x.com/" },
  { label: "LinkedIn", url: "https://www.linkedin.com/" },
  { label: "YouTube", url: "https://www.youtube.com/" },
];

export default function Footer() {
  const footerLogo = "/Assets/logo_white.png";
  const { data: platformSettings } = usePlatformSettingsPublic();
  /** @type {Record<string, any>} */
  const f = platformSettings?.footer && typeof platformSettings.footer === "object" ? platformSettings.footer : {};

  const termsLinks = useMemo(() => {
    const raw = Array.isArray(f.termsLinks) ? f.termsLinks : [];
    return raw
      .map((item) => ({
        label: String(item?.label || "").trim(),
        to: String(item?.to || "").trim(),
      }))
      .filter((item) => item.label && item.to);
  }, [f.termsLinks]);

  const socialLinks = useMemo(() => {
    const raw = Array.isArray(f.socialLinks) ? f.socialLinks : [];
    const list = raw
      .map((item) => ({
        label: String(item?.label || "").trim(),
        url: String(item?.url || "").trim(),
      }))
      .filter((item) => item.label && item.url);
    return list.length ? list : FALLBACK_SOCIAL;
  }, [f.socialLinks]);

  const contactLink = String(f.contactLink || "").trim() || "/contact";
  const cookieSettingsUrl =
    String(f.cookieSettingsUrl || "").trim() || "/patient-profile/cookies";
  const year = new Date().getFullYear();
  const siteName = String(f.copyrightSiteName || "").trim() || "HealBid";

  const email = String(f.supportEmail || "").trim();
  const emailMailto = email ? `mailto:${email}` : "";

  const phoneDisplay = String(f.supportPhoneDisplay || "").trim();
  const phoneHref = phoneHrefFromFooter(f);

  const mailingLabel = String(f.mailingLabel || "").trim() || "Mailing & legal";
  const mailingText = String(f.mailingLinkLabel || "").trim() || "Request details";
  const mailingHref = String(f.mailingLinkHref || "").trim();

  const kicker = String(f.connectKicker || "").trim() || "Connect";
  const title = String(f.contactTitle || "").trim() || "Get in touch";
  const subtitle =
    String(f.contactSubtitle || "").trim() ||
    "We're here to help you navigate healthcare bidding.";
  const tagline =
    String(f.brandTagline || "").trim() ||
    "Transparent healthcare bidding for patients, doctors, and hospitals.";

  const showEmail = Boolean(email && emailMailto);
  const showPhone = Boolean(phoneDisplay && phoneHref);
  const showMailing = Boolean(mailingHref && mailingText);

  return (
    <footer className="healbid-foot">
      <div className="container fotall healbid-foot__inner">
        <div className="healbid-foot__contactRow" aria-labelledby="footer-contact-heading">
          <div className="healbid-foot__contactHead">
            <p className="healbid-foot__contactKicker">{kicker}</p>
            <h3 id="footer-contact-heading" className="healbid-foot__contactTitle">
              {title}
            </h3>
            <p className="healbid-foot__contactSub">{subtitle}</p>
          </div>
          {(showEmail || showPhone || showMailing) && (
            <div className="healbid-foot__contactList">
              {showEmail ? (
                <div className="healbid-foot__contactItem">
                  <span className="healbid-foot__contactIcon">
                    <img src={emil} alt="" />
                  </span>
                  <div>
                    <p className="healbid-foot__contactLabel">Email</p>
                    <a className="healbid-foot__contactLink" href={emailMailto}>
                      {email}
                    </a>
                  </div>
                </div>
              ) : null}
              {showPhone ? (
                <div className="healbid-foot__contactItem">
                  <span className="healbid-foot__contactIcon">
                    <img src={call} alt="" />
                  </span>
                  <div>
                    <p className="healbid-foot__contactLabel">Phone</p>
                    <a className="healbid-foot__contactLink" href={phoneHref}>
                      {phoneDisplay}
                    </a>
                  </div>
                </div>
              ) : null}
              {showMailing ? (
                <div className="healbid-foot__contactItem">
                  <span className="healbid-foot__contactIcon">
                    <img src={location} alt="" />
                  </span>
                  <div>
                    <p className="healbid-foot__contactLabel">{mailingLabel}</p>
                    <FooterLink to={mailingHref} className="healbid-foot__contactLink">
                      {mailingText}
                    </FooterLink>
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </div>

        <div className="healbid-foot__grid">
          <div className="healbid-foot__brandBlock">
            <div className="healbid-foot__brand">
              <img src={footerLogo} alt={siteName} className="healbid-foot__brandLogo" />
            </div>
            <p className="healbid-foot__brandTagline">{tagline}</p>
          </div>

          <div className="healbid-foot__cols">
            <div className="healbid-foot__col">
              <h4 className="healbid-foot__head">Terms &amp; conditions</h4>
              <ul className="healbid-foot__list">
                {termsLinks.map((item) => (
                  <li key={`${item.label}-${item.to}`} className="healbid-foot__li">
                    <FooterLink to={item.to}>{item.label}</FooterLink>
                  </li>
                ))}
                <li className="healbid-foot__li">
                  <FooterLink to={contactLink}>Contact</FooterLink>
                </li>
              </ul>
            </div>
            <div className="healbid-foot__col healbid-foot__col--social">
              <h4 className="healbid-foot__head">Follow us</h4>
              <div className="healbid-foot__social">
                {socialLinks.map((item) => (
                  <a
                    key={item.label}
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${item.label} (opens in new tab)`}
                    className="healbid-foot__icon"
                    title={item.label}
                  >
                    <SocialIcon label={item.label} />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="healbid-foot__bar">
          <div className="healbid-foot__meta">
            <span className="healbid-foot__copy">
              © {year} {siteName}.
            </span>
            <span className="healbid-foot__attribution">
              A product by Eureka Health Care PVT LTD · Developed by PRILIX Labs
            </span>
          </div>

          <div className="healbid-foot__legal">
            <FooterLink to="/ConsentPage" className="healbid-foot__legalLink">
              Privacy &amp; consent
            </FooterLink>
            <FooterLink to={cookieSettingsUrl} className="healbid-foot__legalLink healbid-foot__cookie">
              Cookie settings
            </FooterLink>
          </div>
        </div>
      </div>
    </footer>
  );
}
